import { useEffect, useState } from 'react';
import { isNativePlatform } from '@/lib/platform';
import { error } from '@/utils/logger';

/**
 * Reports whether the device currently has a network connection. On native,
 * @capacitor/network gives an authoritative answer (cellular, wifi, none)
 * and fires on transition. On web, falls back to navigator.onLine plus the
 * standard 'online'/'offline' window events.
 *
 * Initial value: navigator.onLine for SSR-safe defaults (true), then the
 * native status replaces it on mount.
 */
export function useNetworkStatus(): boolean {
    const [online, setOnline] = useState<boolean>(() => {
        if (typeof navigator === 'undefined') return true;
        return navigator.onLine;
    });

    useEffect(() => {
        let cancelled = false;
        let removeNativeListener: (() => void) | null = null;

        if (isNativePlatform()) {
            (async () => {
                try {
                    const { Network } = await import('@capacitor/network');
                    const status = await Network.getStatus();
                    if (cancelled) return;
                    setOnline(status.connected);

                    const handle = await Network.addListener('networkStatusChange', (s) => {
                        setOnline(s.connected);
                    });
                    removeNativeListener = () => {
                        void handle.remove().catch((e) => {
                            error('[useNetworkStatus] failed to remove listener', e);
                        });
                    };
                } catch (e) {
                    error('[useNetworkStatus] @capacitor/network unavailable', e);
                }
            })();
        } else if (typeof window !== 'undefined') {
            const setOn = () => setOnline(true);
            const setOff = () => setOnline(false);
            window.addEventListener('online', setOn);
            window.addEventListener('offline', setOff);
            removeNativeListener = () => {
                window.removeEventListener('online', setOn);
                window.removeEventListener('offline', setOff);
            };
        }

        return () => {
            cancelled = true;
            removeNativeListener?.();
        };
    }, []);

    return online;
}
